/**
 * 把「到点提示音」的判据（`@fht/shared` 的 `advanceEstimateChime`）接到 React 的渲染时机上。
 *
 * 这里**只有接线**，没有任何判定：
 *  - 什么时候该响、只响一次、暂停不响、没有预计时长不响 —— 全在纯函数里（可测）；
 *  - 怎么响（合成什么波形、被浏览器拒绝怎么办）—— 在 `estimateChimeAudio.ts` 里。
 *
 * ## 喂进去的两个数就是界面上显示的那两个数（同源）
 *
 *  - `estimatedSeconds`：界面上「预计 20 分钟」用的那一份（`TaskRow` /
 *    `HomeworkItemRow` 显示的就是它）；
 *  - `elapsedSeconds`：界面上大号计时数字用的那一份。
 *
 * **不另算一套**：界面上写着「预计 20 分钟」，那就必须是 20 分钟那一刻响。
 *
 * ## 为什么状态存在 `useRef` 里
 *
 * "这一次会话已经响过了"是一个**跨渲染的记账**，且它的更新必须与判定发生在同一拍
 * （不能用 `useState`，否则判定与写回之间会隔一次渲染）。用 ref 还有个副作用是好的：
 * React 19 的 `StrictMode` 在开发模式下会把 effect 跑两遍，而第二遍读到的是
 * 已经记账的状态 —— 于是**不会响两声**。
 */
import { useEffect, useRef } from 'react';
import { advanceEstimateChime, initialEstimateChimeState } from '@fht/shared';
import { playEstimateChime } from './estimateChimeAudio';

export interface EstimateChimeParams {
  /** 正在计时的打卡 id；`null` = 没有在计时（没开始 / 暂停 / 已结束）。 */
  sessionId: string | null;
  /** 是否正在计时。 */
  isRunning: boolean;
  /** 界面上那个已用秒数。 */
  elapsedSeconds: number;
  /** 服务端已确认的已用秒数（`actualSeconds`）。 */
  serverSeconds: number;
  /** 界面上那个「预计 X」的秒数；`null` = 没设过 → 不响。 */
  estimatedSeconds: number | null | undefined;
}

/** 到点了就放一声（**只放一次**，见文件头）。 */
export function useEstimateChime(params: EstimateChimeParams): void {
  const { sessionId, isRunning, elapsedSeconds, serverSeconds, estimatedSeconds } = params;
  const stateRef = useRef(initialEstimateChimeState());

  useEffect(() => {
    const decision = advanceEstimateChime(stateRef.current, {
      sessionId,
      isRunning,
      elapsedSeconds,
      serverSeconds,
      estimatedSeconds,
    });
    stateRef.current = decision.state;
    if (decision.shouldChime) {
      // 放不出来（浏览器拒绝自动播放、没有 Web Audio）时 `playEstimateChime`
      // 自己吞掉一切异常 —— 提示音不能变成一次报错或一个弹窗。
      playEstimateChime();
    }
  }, [sessionId, isRunning, elapsedSeconds, serverSeconds, estimatedSeconds]);
}
